import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import AuctionChargeService from './AuctionChargeService';
import { AUCTION_NOT_READY_TEXT } from '../customerProfile/auctionReadinessText';

const modalStyles = {
  overlay: { backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1000 },
  content: {
    top: '50%', left: '50%', right: 'auto', bottom: 'auto',
    maxHeight: '90vh', width: '560px', marginRight: '-50%',
    transform: 'translate(-50%, -50%)', overflow: 'auto',
  },
};

// What each charged line is, for the admin reading the confirm box.
const LINE_TEXT = {
  productPayment: 'ค่าสินค้า',
  transactionFeePaid: 'ค่าบริการ',
  extraJpFeesPaid: 'ค่าธรรมเนียมเพิ่ม',
  shipJpPaid: 'ค่าส่งญี่ปุ่น',
};

const baht = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Confirm box for "เก็บเงิน" (won auctions): what each order is charged now, all ticked
 * (admin can untick), the wallet before/after (may go negative), and a warning when this
 * customer was already charged today. Charges only the ticked orders.
 */
const AuctionChargeModal = ({ isOpen, onClose, group, onCharged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [ticked, setTicked] = useState(new Set());
  const [isCharging, setIsCharging] = useState(false);

  const load = useCallback(async () => {
    setData(null);
    setError('');
    try {
      const orderIds = (group?.orders || []).map((o) => o.id);
      const res = await AuctionChargeService.preview({ customerId: group.customerId, orderIds });
      setData(res.data);
      setTicked(new Set(res.data.items.map((i) => i.orderId)));
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดยอดไม่สำเร็จ');
    }
  }, [group]);

  useEffect(() => {
    if (isOpen && group?.customerId) load();
  }, [isOpen, group, load]);

  const toggle = (orderId) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
    return next;
  });

  const chosen = (data?.items || []).filter((i) => ticked.has(i.orderId));
  const total = round2(chosen.reduce((s, i) => s + i.amount, 0));
  const balanceAfter = data ? round2(data.balance - total) : 0;

  const handleCharge = async () => {
    setIsCharging(true);
    try {
      const res = await AuctionChargeService.charge({
        customerId: group.customerId,
        orderIds: chosen.map((i) => i.orderId),
        expectedTotal: total,
      });
      toast.success(`เก็บเงินแล้ว ฿${baht(res.data.total)} · คงเหลือ ฿${baht(res.data.balanceAfter)}`);
      onCharged?.();
      onClose();
    } catch (err) {
      const body = err.response?.data || {};
      toast.error(body.reason ? `ยังไม่พร้อม: ${AUCTION_NOT_READY_TEXT[body.reason] || body.reason}` : (body.message || 'เก็บเงินไม่สำเร็จ'));
      if (err.response?.status === 409) load(); // total changed / already charged → show the fresh state
    } finally {
      setIsCharging(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={modalStyles} ariaHideApp={false}>
      <div className="p-4">
        <h2 className="text-2xl font-bold">เก็บเงินจาก Wallet</h2>
        <p className="mb-4 text-sm text-gray-500">{group?.contact} · {group?.smileName}</p>

        {error && <p className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        {!data && !error && <p className="text-sm text-gray-500">กำลังโหลด...</p>}

        {data && (
          <div className="space-y-3">
            {!data.ready && (
              <p className="rounded bg-red-50 p-3 text-sm font-semibold text-red-700">
                ❌ ยังเก็บเงินไม่ได้: {AUCTION_NOT_READY_TEXT[data.reason] || data.reason}
              </p>
            )}
            {data.chargedToday.count > 0 && (
              <p className="rounded bg-amber-50 p-3 text-sm text-amber-800">
                ⚠ ลูกค้านี้ถูกเก็บเงินประมูลวันนี้แล้ว {data.chargedToday.count} รายการ ฿{baht(data.chargedToday.amount)}
              </p>
            )}

            <table className="w-full text-sm">
              <tbody>
                {data.items.map((i) => (
                  <tr key={i.orderId} className="border-b align-top">
                    <td className="py-2 pr-2">
                      <input type="checkbox" checked={ticked.has(i.orderId)} onChange={() => toggle(i.orderId)} />
                    </td>
                    <td className="py-2">
                      <div className="font-medium">{i.orderCode}</div>
                      <div className="text-xs text-gray-500">
                        {i.lines.map((l) => `${LINE_TEXT[l.amountType] || l.amountType} ${baht(l.amount)}`).join(' · ')}
                      </div>
                    </td>
                    <td className="py-2 text-right font-medium">฿{baht(i.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-500">
              ยังไม่เก็บตอนนี้: น้ำหนัก ค่าส่งในไทย และค่าส่งญี่ปุ่นที่ยังไม่ทราบ/COD — เก็บตอนของถึงไทย
            </p>

            <div className="rounded-lg bg-gray-50 p-3 text-sm">
              <div className="flex justify-between"><span>ยอดเก็บ</span><span className="font-bold">฿{baht(total)}</span></div>
              <div className="flex justify-between"><span>Wallet ตอนนี้</span><span>฿{baht(data.balance)}</span></div>
              <div className="flex justify-between">
                <span>Wallet หลังเก็บ</span>
                <span className={balanceAfter < 0 ? 'font-bold text-red-600' : ''}>฿{baht(balanceAfter)}</span>
              </div>
              {balanceAfter < 0 && <p className="mt-1 text-xs text-red-600">ติดลบ — ลูกค้าต้องเติมเงินเพื่อชำระยอดค้าง</p>}
            </div>

            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-md border px-4 py-2">ยกเลิก</button>
              <button
                type="button"
                onClick={handleCharge}
                disabled={!data.ready || chosen.length === 0 || isCharging}
                className="rounded-md bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isCharging ? 'กำลังเก็บเงิน...' : `ยืนยันเก็บเงิน ฿${baht(total)}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AuctionChargeModal;
